import PropTypes from 'prop-types';
import React, { useState, useContext, useCallback } from 'react';
import { Input } from '@eika/input';
import DropDown from '../../../drop-down';
import getLabel, { getHelpBody } from '../labels';
import RadioInput from '../rr/radioinput';
import CountryContext from '../../../country-context';
import { getCitizenshipList } from '../../helper/component-helper';
import { CITIZENSHIP, SECOND_CITIZENSHIP } from '../keys-constants';
import ErrorMessage from '../../errormessage';

export const getDisponentDetaljerFields = () => ({
    navn: null,
    fnr: null,
    fodeland: null,
    statsborgerskap: null,
    sekundartStatsborgerskap: null,
    adresse: null,
    postnummer: null,
    poststed: null,
    bostedsland: null,
    pep: null,
    pepVerv: null,
    pepRelasjon: null,
});

function DisponentDetaljer({ errors, question, onChange, tekster = {}, index, appNamespace }) {
    const countryContext = useContext(CountryContext);
    const bostedslandOptions = countryContext.getBostedslandOptions();
    const fodelandOptions = countryContext.getFodelandOptions();

    const [citizenship, setCitizenship] = useState(question.statsborgerskap);
    const [secondCitizenship, setSecondCitizenship] = useState(question.sekundartStatsborgerskap);
    const listOfCountries = countryContext.getStatsborgerskapOptions();

    const getHandleChange = useCallback(
        (key, dataKey = 'value') =>
            (event) => {
                let newVal = event?.target?.[dataKey] ?? event;
                if (event?.target?.name?.includes('fnr')) {
                    newVal = `${newVal}`.replace(new RegExp(/[^0-9]/, 'g'), '');
                }
                if (dataKey === 'checked') {
                    newVal = event.target.value !== 'false';
                }
                if (key === CITIZENSHIP) {
                    setCitizenship(newVal);
                }
                if (key === SECOND_CITIZENSHIP) {
                    setSecondCitizenship(newVal);
                }
                onChange({ [key]: newVal });
            },
        [onChange]
    );

    const removeValue = useCallback(
        (key) => {
            if (key === CITIZENSHIP) {
                setCitizenship(undefined);
            }
            if (key === SECOND_CITIZENSHIP) {
                setSecondCitizenship(undefined);
            }
            onChange({ [key]: undefined });
        },
        [onChange]
    );

    return (
        <>
            <div className="skjema-input-field-wrap-small skjema-input-field-wrap">
                <Input
                    className="disponent-field-navn"
                    name={`navn-disponent-${index}`}
                    onChange={getHandleChange('navn')}
                    value={question.navn || ''}
                    modalHelpHeader={tekster.disp_navn_infotittel}
                    modalHelpBody={getHelpBody(tekster, 'disp_navn_info')}
                    type="text"
                    label={getLabel(tekster, 'disp_navn')}
                />
                {errors.includes('navn') && <ErrorMessage text={getLabel(tekster, 'disp_navn_instruks')} />}
            </div>
            <div className="skjema-input-field-wrap-small skjema-input-field-wrap">
                <Input
                    className="disponent-field-fnr"
                    name={`fnr-disponent-${index}`}
                    onChange={getHandleChange('fnr')}
                    value={question.fnr || ''}
                    modalHelpHeader={tekster.disp_fnr_infotittel}
                    modalHelpBody={getHelpBody(tekster, 'disp_fnr_info')}
                    type="text"
                    label={getLabel(tekster, 'disp_fnr')}
                />
                {errors.includes('fnr') && <ErrorMessage text={getLabel(tekster, 'disp_fnr_instruks')} />}
            </div>
            <div className="skjema-input-field-wrap-small skjema-input-field-wrap">
                <DropDown
                    name={`birth-country-disponent-${index}`}
                    values={fodelandOptions}
                    selectValue={getHandleChange('fodeland')}
                    selected={question.fodeland || ''}
                    modalHelpHeader={tekster.disp_fodeland_infotittel}
                    modalHelpBody={getHelpBody(tekster, 'disp_fodeland_info')}
                    removeValue={() => removeValue('fodeland')}
                    label={getLabel(tekster, 'disp_fodeland')}
                />
                {errors.includes('fodeland') && <ErrorMessage text={getLabel(tekster, 'disp_fodeland_instruks')} />}
            </div>
            <div className="skjema-input-field-wrap-small skjema-input-field-wrap">
                <DropDown
                    name={`citizenship-disponent-${index}`}
                    values={getCitizenshipList(listOfCountries, secondCitizenship)}
                    selectValue={getHandleChange('statsborgerskap')}
                    selected={question.statsborgerskap || ''}
                    modalHelpHeader={tekster.disp_statsborgerskap_infotittel}
                    modalHelpBody={getHelpBody(tekster, 'disp_statsborgerskap_info')}
                    removeValue={() => removeValue('statsborgerskap')}
                    label={getLabel(tekster, 'disp_statsborgerskap')}
                />
                {errors.includes('statsborgerskap') && (
                    <ErrorMessage text={getLabel(tekster, 'disp_statsborgerskap_instruks')} />
                )}
            </div>
            <div className="skjema-input-field-wrap-small skjema-input-field-wrap">
                <DropDown
                    name={`second-citizenship-disponent-${index}`}
                    values={getCitizenshipList(listOfCountries, citizenship)}
                    selectValue={getHandleChange('sekundartStatsborgerskap')}
                    selected={question.sekundartStatsborgerskap || ''}
                    modalHelpHeader={tekster.disp_statsborgerskap2_infotittel}
                    modalHelpBody={getHelpBody(tekster, 'disp_statsborgerskap2_info')}
                    removeValue={() => removeValue('sekundartStatsborgerskap')}
                    label={getLabel(tekster, 'disp_statsborgerskap2')}
                />
            </div>
            <div className="skjema-input-field-wrap-small skjema-input-field-wrap">
                <Input
                    className="disponent-field-adresse"
                    name={`adresse-disponent-${index}`}
                    onChange={getHandleChange('adresse')}
                    value={question.adresse || ''}
                    modalHelpHeader={tekster.disp_adresse_infotittel}
                    modalHelpBody={getHelpBody(tekster, 'disp_adresse_info')}
                    type="text"
                    label={getLabel(tekster, 'disp_adresse')}
                />
                {errors.includes('adresse') && <ErrorMessage text={getLabel(tekster, 'disp_adresse_instruks')} />}
            </div>
            <div className="skjema-input-field-wrap-small skjema-input-field-wrap">
                <div className="row row-no-gutters">
                    <div className="col-xs-4">
                        <Input
                            className="disponent-field-postnummer"
                            name={`postnummer-disponent-${index}`}
                            onChange={getHandleChange('postnummer')}
                            value={question.postnummer || ''}
                            modalHelpHeader={tekster.disp_postnummer_infotittel}
                            modalHelpBody={getHelpBody(tekster, 'disp_postnummer_info')}
                            type="number"
                            label={getLabel(tekster, 'disp_postnummer')}
                            inputMode="decimal"
                        />
                        {errors.includes('postnummer') && (
                            <ErrorMessage text={getLabel(tekster, 'disp_postnummer_instruks')} />
                        )}
                    </div>
                    <div className="col-xs-7 col-xs-offset-1">
                        <Input
                            className="disponent-field-poststed"
                            name={`poststed-disponent-${index}`}
                            onChange={getHandleChange('poststed')}
                            value={question.poststed || ''}
                            modalHelpHeader={tekster.disp_poststed_infotittel}
                            modalHelpBody={getHelpBody(tekster, 'disp_poststed_info')}
                            type="text"
                            label={getLabel(tekster, 'disp_poststed')}
                        />
                        {errors.includes('poststed') && (
                            <ErrorMessage text={getLabel(tekster, 'disp_poststed_instruks')} />
                        )}
                    </div>
                </div>
            </div>
            <div className="skjema-input-field-wrap-small skjema-input-field-wrap">
                <DropDown
                    name={`residence-disponent-${index}`}
                    values={bostedslandOptions}
                    selectValue={getHandleChange('bostedsland')}
                    selected={question.bostedsland || ''}
                    modalHelpHeader={tekster.disp_bostedsland_infotittel}
                    modalHelpBody={getHelpBody(tekster, 'disp_bostedsland_info')}
                    removeValue={() => removeValue('bostedsland')}
                    label={getLabel(tekster, 'disp_bostedsland')}
                />
                {errors.includes('bostedsland') && (
                    <ErrorMessage text={getLabel(tekster, 'disp_bostedsland_instruks')} />
                )}
            </div>
            <div className="skjema-input-field-wrap-small skjema-input-field-wrap">
                <RadioInput
                    heading={getLabel(tekster, 'disp_pep')}
                    id={question.disponentKey}
                    labels={['Ja', 'Nei']}
                    name={`pep-disponent-${index}`}
                    onChange={getHandleChange('pep', 'checked')}
                    values={[true, false]}
                    value={question.pep}
                    modalHelpHeader={tekster.disp_pep_infotittel}
                    modalHelpBody={getHelpBody(tekster, 'disp_pep_info')}
                    appNamespace={appNamespace}
                />
                {errors.includes('pep') && <ErrorMessage text={getLabel(tekster, 'disp_pep_instruks')} />}
            </div>
            {question.pep && (
                <>
                    <div className="skjema-input-field-wrap-small skjema-input-field-wrap">
                        <Input
                            className="disponent-field-pep-svar-verv"
                            name={`pepVerv-disponent-${index}`}
                            onChange={getHandleChange('pepVerv')}
                            value={question.pepVerv || ''}
                            modalHelpHeader={tekster.disp_peptype_infotittel}
                            modalHelpBody={getHelpBody(tekster, 'disp_peptype_info')}
                            type="text"
                            label={getLabel(tekster, 'disp_peptype')}
                        />
                        {errors.includes('pepVerv') && (
                            <ErrorMessage text={getLabel(tekster, 'disp_peptype_instruks')} />
                        )}
                    </div>
                    <div className="skjema-input-field-wrap-small skjema-input-field-wrap">
                        <Input
                            className="disponent-field-pep-svar-relasjon"
                            name={`pepRelasjon-disponent-${index}`}
                            onChange={getHandleChange('pepRelasjon')}
                            value={question.pepRelasjon || ''}
                            modalHelpHeader={tekster.disp_peprelasjon_infotittel}
                            modalHelpBody={getHelpBody(tekster, 'disp_peprelasjon_info')}
                            type="text"
                            label={getLabel(tekster, 'disp_peprelasjon')}
                        />
                        {errors.includes('pepRelasjon') && (
                            <ErrorMessage text={getLabel(tekster, 'disp_peprelasjon_instruks')} />
                        )}
                    </div>
                </>
            )}
        </>
    );
}

DisponentDetaljer.propTypes = {
    appNamespace: PropTypes.string,
    index: PropTypes.number,
    errors: PropTypes.arrayOf(PropTypes.string),
    question: PropTypes.shape({
        disponentKey: PropTypes.string,
        navn: PropTypes.string,
        fnr: PropTypes.string,
        fodeland: PropTypes.string,
        statsborgerskap: PropTypes.string,
        sekundartStatsborgerskap: PropTypes.string,
        adresse: PropTypes.string,
        postnummer: PropTypes.string,
        poststed: PropTypes.string,
        bostedsland: PropTypes.string,
        pep: PropTypes.bool,
        pepVerv: PropTypes.string,
        pepRelasjon: PropTypes.string,
    }).isRequired,
    onChange: PropTypes.func.isRequired,
    tekster: PropTypes.shape({
        disp_adresse_info: PropTypes.string,
        disp_adresse_infotittel: PropTypes.string,
        disp_adresse_instruks: PropTypes.string,
        disp_adresse: PropTypes.string,
        disp_bostedsland_info: PropTypes.string,
        disp_bostedsland_infotittel: PropTypes.string,
        disp_bostedsland_instruks: PropTypes.string,
        disp_bostedsland: PropTypes.string,
        disp_fnr_info: PropTypes.string,
        disp_fnr_infotittel: PropTypes.string,
        disp_fnr_instruks: PropTypes.string,
        disp_fnr: PropTypes.string,
        disp_fodeland_info: PropTypes.string,
        disp_fodeland_infotittel: PropTypes.string,
        disp_fodeland_instruks: PropTypes.string,
        disp_fodeland: PropTypes.string,
        disp_navn_info: PropTypes.string,
        disp_navn_infotittel: PropTypes.string,
        disp_navn_instruks: PropTypes.string,
        disp_navn: PropTypes.string,
        disp_pep_info: PropTypes.string,
        disp_pep_infotittel: PropTypes.string,
        disp_pep_instruks: PropTypes.string,
        disp_pep: PropTypes.string,
        disp_peprelasjon_info: PropTypes.string,
        disp_peprelasjon_infotittel: PropTypes.string,
        disp_peprelasjon_instruks: PropTypes.string,
        disp_peprelasjon: PropTypes.string,
        disp_peptype_info: PropTypes.string,
        disp_peptype_infotittel: PropTypes.string,
        disp_peptype_instruks: PropTypes.string,
        disp_peptype: PropTypes.string,
        disp_postnummer_info: PropTypes.string,
        disp_postnummer_infotittel: PropTypes.string,
        disp_postnummer_instruks: PropTypes.string,
        disp_postnummer: PropTypes.string,
        disp_poststed_info: PropTypes.string,
        disp_poststed_infotittel: PropTypes.string,
        disp_poststed_instruks: PropTypes.string,
        disp_poststed: PropTypes.string,
        disp_statsborgerskap_info: PropTypes.string,
        disp_statsborgerskap_infotittel: PropTypes.string,
        disp_statsborgerskap_instruks: PropTypes.string,
        disp_statsborgerskap: PropTypes.string,
        disp_statsborgerskap2_info: PropTypes.string,
        disp_statsborgerskap2_infotittel: PropTypes.string,
        disp_statsborgerskap2_instruks: PropTypes.string,
        disp_statsborgerskap2: PropTypes.string,
    }).isRequired,
};

export default DisponentDetaljer;
